// #4816 — did this workout beat a personal record? The review prompt's phase 2
// moment, decided as a pure rule so every case can be tested on its own.
//
// "A personal record" means a working set whose estimated one-rep max STRICTLY
// beats the best the person ever reached on that exercise in any OTHER workout.
// That best comes from the `bestStrengthByExercise` view, not from the stored
// per-exercise estimate: since #5174 that stored value is the last session's
// best and can go down, so beating it would fire on nearly every workout that
// progressed at all.
//
// See design-docs/review-prompts.md, "Phase 2", for why the spec's suggestion and
// the strength test were both rejected.

import { isStrengthTestSetType, Log_WorkoutExercise, Log_WorkoutSet } from './data-model/log-workout';
import { LogRecordState } from './data-model/user-log';

/**
 * The estimate a set offers as a record, or `undefined` when the set cannot be
 * one: not finished, a strength-test set, or no usable estimate.
 *
 * Test sets are excluded because a test is a calibration rather than a
 * comparison, it often follows a setback, and one form of it happens mid-workout.
 */
function recordCandidate_net_kg(set: Log_WorkoutSet | undefined): number | undefined {
  // if the set is not a finished working set, it cannot be a record
  if (!set || set.state !== LogRecordState.Done || isStrengthTestSetType(set.setType)) return undefined;
  const estimate_net_kg = set.stats?.estimated1rm_net_kg;
  // if the estimate is missing or unusable, there is nothing to compare
  if (typeof estimate_net_kg !== 'number' || !isFinite(estimate_net_kg) || estimate_net_kg <= 0) return undefined;
  return estimate_net_kg;
}

/**
 * The exercises in this workout that hold at least one set able to be a record.
 * Only these are worth looking up, so a workout of test sets reads nothing.
 */
export function exercisesWithRecordCandidates(exercises: Log_WorkoutExercise[]): string[] {
  const ids = new Set<string>();
  for (const exercise of exercises || []) {
    // if the exercise has no id, there is nothing to look its best up by
    if (!exercise?.exerciseId) continue;
    if ((exercise.sets || []).some(set => recordCandidate_net_kg(set) !== undefined)) ids.add(exercise.exerciseId);
  }
  return [...ids];
}

/**
 * True when some working set in this workout strictly beats that exercise's best
 * from every other workout.
 *
 * An exercise with no earlier best is NOT a record: beating nothing is not an
 * achievement, and somebody's first session would otherwise fire on every
 * exercise they try. Equalling the earlier best is not a record either.
 */
export function workoutBeatAPersonalRecord(
  exercises: Log_WorkoutExercise[],
  bestElsewhere_net_kg: Map<string, number>,
): boolean {
  return personalRecordReport(exercises, bestElsewhere_net_kg).some(row => row.beat);
}

/** One exercise's answer, for the test-mode tool that explains the decision. */
export interface PersonalRecordRow {
  exerciseId: string;
  /** The best working-set estimate in this workout. */
  thisWorkout_net_kg: number;
  /** The best from every other workout, absent when there is none. */
  earlierBest_net_kg?: number;
  /** Did this workout strictly beat it? Never true without an earlier best. */
  beat: boolean;
}

/**
 * The same decision as `workoutBeatAPersonalRecord`, one row per exercise that
 * held a working set, so a person can see why it came out as it did.
 */
export function personalRecordReport(
  exercises: Log_WorkoutExercise[],
  bestElsewhere_net_kg: Map<string, number>,
): PersonalRecordRow[] {
  const rows = new Map<string, PersonalRecordRow>();
  for (const exercise of exercises || []) {
    // if the exercise has no id, there is nothing to look its best up by
    if (!exercise?.exerciseId) continue;
    for (const set of exercise.sets || []) {
      const candidate_net_kg = recordCandidate_net_kg(set);
      if (candidate_net_kg === undefined) continue;
      // the same exercise can sit in two places in one workout; keep its best set
      const row = rows.get(exercise.exerciseId);
      if (row && row.thisWorkout_net_kg >= candidate_net_kg) continue;
      const earlierBest_net_kg = bestElsewhere_net_kg.get(exercise.exerciseId);
      rows.set(exercise.exerciseId, {
        exerciseId: exercise.exerciseId,
        thisWorkout_net_kg: candidate_net_kg,
        earlierBest_net_kg,
        beat: earlierBest_net_kg !== undefined && candidate_net_kg > earlierBest_net_kg,
      });
    }
  }
  return [...rows.values()];
}
